import * as React from 'react';
import moment from 'moment';

interface Skill {
  id: string | number;
  name: string;
  category: string;
  color: string;
}

interface Category {
  name: string;
  color: string;
  count: number;
}

interface JobData {
  position_type: string;
  temp_date?: string;
  start_date?: string;
  start_date_readable?: string;
  benefit_medical?: boolean;
  benefit_dental?: boolean;
  benefit_vision?: boolean;
  benefit_401k?: boolean;
  benefit_pto?: boolean;
  benefit_bonuses?: boolean;
}

interface ProfileData {
  position_types: Array<string>;
}

interface SkillsProps {
  skills: Skill[];
  type: 'job' | 'profile';
  data: JobData | ProfileData;
  className?: string;
  style?: React.CSSProperties;
  useContainer?: boolean; // Whether to wrap in a flex container
  showPositionTypes?: boolean; // Whether to show position type chips (default: true)
  /** 'solid' keeps the classic colored-background badges; 'soft' renders quiet
   *  outlined chips with a small dot carrying the category color — for dense
   *  list rows where a wall of saturated pills reads as noise. */
  variant?: 'solid' | 'soft';
}

function removeDuplicates(arr: Category[], prop: keyof Category): Category[] {
  const obj: { [key: string]: Category } = {};
  for (let i = 0, len = arr.length; i < len; i++) {
    const key = String(arr[i][prop]);
    if (!obj[key]) obj[key] = arr[i];
  }
  const newArr: Category[] = [];
  for (const key in obj) newArr.push(obj[key]);
  return newArr;
}

/**
 * Skills Component - Shared between Jobs and Profiles
 * 
 * Displays skills with categories, position types, and benefits.
 * Can be used for both job postings and candidate profiles.
 * 
 * Features:
 * - Skill categorization with color coding
 * - Position type badges (Full Time, Part Time, Temp, etc.)
 * - Job benefits display (Medical, Dental, Vision, etc.)
 * - Responsive design with proper spacing
 */
export const Skills: React.FC<SkillsProps> = ({
  skills,
  type,
  data,
  className = '',
  style = {},
  useContainer = true,
  showPositionTypes = true,
  variant = 'solid'
}) => {
  // Get Unique Categories
  let categories = skills.map(skill => ({
    name: skill.category,
    color: skill.color,
    count: 0
  }));

  // Count Category Occurrences
  categories = categories.map(c => ({
    ...c,
    count: skills.filter(s => s.category === c.name).length
  }));

  categories = removeDuplicates(categories, 'name');

  // Use consistent Bootstrap classes for skill badges
  const skillClasses = variant === 'soft'
    ? "skill-chip skill-chip--type"
    : "text-light rounded px-2 py-1 me-1 mb-1";

  const containerClasses = "d-flex my-1 flex-wrap";

  const renderPositionTypes = () => {
    if (type === 'job') {
      const jobData = data as JobData;
      return (
        <>
          {jobData.position_type === "fulltime" && (
            <span className={`full-time ${skillClasses}`}>Full Time</span>
          )}
          {jobData.position_type === "parttime" && (
            <span className={`part-time ${skillClasses}`}>Part Time</span>
          )}
          {jobData.position_type === "longtermtemp" && (
            <span className={`part-time ${skillClasses}`}>
              Long Term Temp {jobData.start_date_readable ? jobData.start_date_readable : (jobData.start_date ? moment(jobData.start_date).format('MMMM Do') : '')}
            </span>
          )}
          {jobData.position_type === "temp" && (
            <span className={`temp ${skillClasses}`}>Temp {moment(jobData.temp_date).format("MMMM Do")}</span>
          )}
        </>
      );
    } else {
      const profileData = data as ProfileData;
      return (
        <>
          {profileData.position_types.includes("Full Time") && (
            <span className={`full-time ${skillClasses}`}>Full Time</span>
          )}
          {profileData.position_types.includes("Part Time") && (
            <span className={`part-time ${skillClasses}`}>Part Time</span>
          )}
          {profileData.position_types.includes("Long Term Temp") && (
            <span className={`part-time ${skillClasses}`}>Long Term Temp</span>
          )}
          {profileData.position_types.includes("Temp") && (
            <span className={`temp ${skillClasses}`}>Temp</span>
          )}
        </>
      );
    }
  };

  const renderBenefits = () => {
    if (type !== 'job') return null;

    const jobData = data as JobData;
    return (
      <>
        {jobData.benefit_medical && (<span className={`benefit ${skillClasses}`}>Medical</span>)}
        {jobData.benefit_dental && (<span className={`benefit ${skillClasses}`}>Dental</span>)}
        {jobData.benefit_vision && (<span className={`benefit ${skillClasses}`}>Vision</span>)}
        {jobData.benefit_401k && (<span className={`benefit ${skillClasses}`}>401k</span>)}
        {jobData.benefit_pto && (<span className={`benefit ${skillClasses}`}>PTO</span>)}
        {jobData.benefit_bonuses && (<span className={`benefit ${skillClasses}`}>Bonuses</span>)}
      </>
    );
  };

  const renderSkills = () => {
    return categories.map(cat => {
      const single = cat.count === 1 ? skills.filter(s => s.category === cat.name)[0] : null;
      // Soft chips have room for the full category name; solid keeps the
      // legacy first-word truncation on profiles.
      const label = single
        ? single.name
        : `(${cat.count}) ${type === 'profile' && variant !== 'soft' ? cat.name.split(' ')[0] : cat.name}`;

      if (variant === 'soft') {
        return (
          <span key={`cat-${cat.name}`} className="skill-chip" title={cat.name}>
            <span className="skill-chip__dot" style={{ backgroundColor: cat.color }} aria-hidden="true"></span>
            {label}
          </span>
        );
      }

      // skill.id and a bare category index can collide (both can be `1`),
      // producing duplicate React keys — prefix to keep the namespaces apart.
      return (
        <span
          key={`cat-${cat.name}`}
          className={`skill-badge ${skillClasses}`}
          style={{ backgroundColor: cat.color }}
        >
          {label}
        </span>
      );
    });
  };

  const content = (
    <>
      {showPositionTypes && renderPositionTypes()}
      {renderBenefits()}
      {renderSkills()}
    </>
  );

  if (useContainer) {
    return (
      <div className={`${containerClasses} ${className}`} style={style}>
        {content}
      </div>
    );
  }

  return content;
};

export default Skills;
